import {SESSION_ROUTE_LOOP_THRESHOLD} from "@/lib/SessionResolver";

export const SESSION_ROUTE_LOOP_WINDOW_MS = 20_000;
const STORAGE_KEY = "braiv:sessionRouteLoop";

type LoopState = {
    count: number;
    startedAt: number;
    lastRoute: string;
};

function readState(): LoopState | null {
    if (typeof window === "undefined") {
        return null;
    }
    try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw) as LoopState;
        if (!parsed || typeof parsed.count !== "number" || typeof parsed.startedAt !== "number") {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}

function writeState(state: LoopState): void {
    if (typeof window === "undefined") {
        return;
    }
    try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
        // Ignore quota / private-mode failures.
    }
}

export function peekSessionRouteLoopCount(): number {
    const state = readState();
    if (!state) {
        return 0;
    }
    if (Date.now() - state.startedAt > SESSION_ROUTE_LOOP_WINDOW_MS) {
        return 0;
    }
    return state.count;
}

export function recordSessionRouteNavigation(route: string): number {
    const now = Date.now();
    const existing = readState();
    if (!existing || now - existing.startedAt > SESSION_ROUTE_LOOP_WINDOW_MS) {
        const next = {count: 1, startedAt: now, lastRoute: route};
        writeState(next);
        return next.count;
    }
    if (existing.lastRoute === route) {
        return existing.count;
    }
    const next = {
        count: existing.count + 1,
        startedAt: existing.startedAt,
        lastRoute: route,
    };
    writeState(next);
    return next.count;
}

export function resetSessionRouteLoop(): void {
    if (typeof window === "undefined") {
        return;
    }
    try {
        window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
        // ignore
    }
}

export function isSessionRouteLoopTripped(count = peekSessionRouteLoopCount()): boolean {
    return count >= SESSION_ROUTE_LOOP_THRESHOLD;
}
